import { useEffect } from 'react';
import {
	Alert,
	Button,
	Form,
	Input,
	InputNumber,
	Modal,
	Select,
	Switch,
	notification,
} from 'antd';

import {
	INSTRUCTIONS_MAX_LENGTH,
	PROCESS_LABELS,
	type ProcessKind,
	type ProcessSpec,
} from '../../../../interfaces/ProcessCatalog.Interface';
import {
	useCreateProcessSpecMutation,
	useDeleteProcessSpecMutation,
	useUpdateProcessSpecMutation,
} from '../../../../redux/api/processCatalog';
import { extractApiError } from '../../../../utils/apiError';

interface Props {
	open: boolean;
	kind: ProcessKind;
	/** The row being edited, or `null` to create a new one. */
	spec: ProcessSpec | null;
	onClose: () => void;
}

interface FormValues {
	name: string;
	behavior: ProcessSpec['behavior'];
	batch_mode: ProcessSpec['batch_mode'];
	instructions: string;
	match_tokens: string[];
	is_default: boolean;
	is_active: boolean;
	sort_order: number;
}

export default function ProcessSpecFormModal({
	open,
	kind,
	spec,
	onClose,
}: Props) {
	const [form] = Form.useForm<FormValues>();
	const [createSpec, { isLoading: isCreating }] = useCreateProcessSpecMutation();
	const [updateSpec, { isLoading: isUpdating }] = useUpdateProcessSpecMutation();
	const [deleteSpec, { isLoading: isDeleting }] = useDeleteProcessSpecMutation();

	// Watched so the instruction and batching fields can disappear for a
	// do-nothing row, where they mean nothing and only invite confusion.
	const behavior = Form.useWatch('behavior', form) ?? 'do_nothing';
	const isSpecial = behavior === 'special';

	useEffect(() => {
		if (!open) return;
		form.setFieldsValue({
			name: spec?.name ?? '',
			behavior: spec?.behavior ?? 'do_nothing',
			batch_mode: spec?.batch_mode ?? 'none',
			instructions: spec?.instructions ?? '',
			match_tokens: spec?.match_tokens ?? [],
			is_default: spec?.is_default ?? false,
			is_active: spec?.is_active ?? true,
			sort_order: spec?.sort_order ?? 0,
		});
	}, [open, spec, form]);

	async function submit(values: FormValues) {
		// A do-nothing row's instructions and batching are never read; clearing
		// them here means switching a row back to Do nothing does not leave
		// invisible text behind to reappear later.
		const body = {
			kind,
			...values,
			instructions: isSpecial ? values.instructions : '',
			batch_mode: isSpecial ? values.batch_mode : ('none' as const),
			match_tokens: kind === 'finishing' ? values.match_tokens : [],
			is_default: kind === 'finishing' ? false : values.is_default,
		};

		try {
			if (spec) {
				await updateSpec({ id: spec.id, body }).unwrap();
			} else {
				await createSpec(body).unwrap();
			}
			notification.success({
				message: `${PROCESS_LABELS[kind]} ${spec ? 'updated' : 'created'}`,
			});
			onClose();
		} catch (error) {
			notification.error({ message: extractApiError(error) });
		}
	}

	async function remove() {
		if (!spec) return;
		try {
			await deleteSpec(spec.id).unwrap();
			notification.success({ message: `${spec.name} deleted` });
			onClose();
		} catch (error) {
			notification.error({ message: extractApiError(error) });
		}
	}

	return (
		<Modal
			open={open}
			title={`${spec ? 'Edit' : 'New'} ${PROCESS_LABELS[kind]}`}
			onCancel={onClose}
			width={640}
			footer={[
				spec ? (
					<Button
						key="delete"
						danger
						loading={isDeleting}
						onClick={remove}
						style={{ float: 'left' }}
					>
						Delete
					</Button>
				) : null,
				<Button key="cancel" onClick={onClose}>
					Cancel
				</Button>,
				<Button
					key="save"
					type="primary"
					loading={isCreating || isUpdating}
					onClick={() => form.submit()}
				>
					Save
				</Button>,
			]}
		>
			<Form form={form} layout="vertical" onFinish={submit}>
				<Form.Item
					name="name"
					label="Name"
					rules={[{ required: true, message: 'Name is required' }]}
					tooltip="Printed on the tag and shown in the shop floor app."
				>
					<Input placeholder="e.g. Bead Blasting" />
				</Form.Item>

				<Form.Item
					name="behavior"
					label="Behavior"
					tooltip="Do nothing is the ordinary case: no tags, no highlighting on the tablet, no operator tasks."
				>
					<Select
						options={[
							{ value: 'do_nothing', label: 'Do nothing' },
							{ value: 'special', label: 'Special process' },
						]}
					/>
				</Form.Item>

				{kind === 'finishing' && (
					<Form.Item
						name="match_tokens"
						label="Surface finish match tokens"
						tooltip="Matched as substrings against the line item's surface finish once case and punctuation are stripped, so 'beadblast' catches 'Bead Blast' and 'bead-blasted'. Never matched against coating."
					>
						<Select
							mode="tags"
							tokenSeparators={[',']}
							placeholder="e.g. beadblast, tumble"
						/>
					</Form.Item>
				)}

				{isSpecial && (
					<>
						<Form.Item
							name="batch_mode"
							label="Batching"
							tooltip="Quality Interval + Minimum Batch prints one tag per batch and fires one shop floor task per batch. No batching, one tag prints a single tag for the job without a batch task. None still shows the instructions but prints no tags."
						>
							<Select
								options={[
									{ value: 'none', label: 'None (no tags)' },
									{
										value: 'one_tag',
										label: 'No batching, one tag',
									},
									{
										value: 'quality_interval',
										label: 'Quality Interval + Minimum Batch',
									},
								]}
							/>
						</Form.Item>

						<Form.Item
							name="instructions"
							label="Operator instructions"
							tooltip="Printed on the tag and shown to the operator on the tablet."
							rules={[
								{
									max: INSTRUCTIONS_MAX_LENGTH,
									message: `Keep instructions to ${INSTRUCTIONS_MAX_LENGTH} characters so they fit on the printed tag.`,
								},
							]}
						>
							<Input.TextArea
								rows={4}
								maxLength={INSTRUCTIONS_MAX_LENGTH}
								showCount
								placeholder="e.g. Bead blast all exterior faces to an even matte."
							/>
						</Form.Item>
					</>
				)}

				{kind !== 'finishing' && (
					<Form.Item
						name="is_default"
						label="Default for new line items"
						valuePropName="checked"
						tooltip="New line items copy this row. Changing it does not rewrite line items that already exist."
					>
						<Switch />
					</Form.Item>
				)}

				<Form.Item
					name="is_active"
					label="Active"
					valuePropName="checked"
					tooltip="Inactive rows stay on the jobs already carrying them but cannot be picked."
				>
					<Switch />
				</Form.Item>

				<Form.Item
					name="sort_order"
					label="Sort order"
					tooltip="Order shown in pickers, and the order finish tokens are matched in — list a narrow token before a broad one."
				>
					<InputNumber min={0} />
				</Form.Item>

				{kind === 'finishing' && (
					<Alert
						type="info"
						showIcon
						message="Finishing has no default"
						description="It is resolved from the line item's surface finish, so a finish that matches nothing simply needs nothing."
					/>
				)}
			</Form>
		</Modal>
	);
}
